import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

const BasketManagement = () => {
  const [baskets, setBaskets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedBasket, setSelectedBasket] = useState(null);
  const { currentUser } = useAuth();

  // Form states
  const [basketType, setBasketType] = useState('fresh');
  const [basketNumber, setBasketNumber] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [length, setLength] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchBaskets();
  }, []);

  const fetchBaskets = async () => {
    try {
      setLoading(true);
      const basketsRef = collection(db, 'stock_basket');
      const q = query(basketsRef, orderBy('created_at', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const basketsData = [];
      
      for (const docSnap of querySnapshot.docs) {
        const basketData = {
          id: docSnap.id,
          ...docSnap.data()
        };
        
        // Fetch products in this basket
        const stockProductsRef = collection(db, 'stock_basket', docSnap.id, 'stock_product');
        const stockSnapshot = await getDocs(stockProductsRef);
        
        let totalVolume = 0;
        const productsInBasket = {};
        let validProductCount = 0;
        
        stockSnapshot.forEach((stockDoc) => {
          const product = stockDoc.data();
          
          // ✅ FIX: Filter based on basket type
          const isReturnProduct = product.is_return === true;
          const isReturnBasket = basketData.basket_type === 'return';
          
          // For return basket, only count return products
          // For fresh basket, only count fresh products (non-return)
          if (isReturnBasket && !isReturnProduct) return;
          if (!isReturnBasket && isReturnProduct) return;
          
          const volume = (product.width || 0) * (product.height || 0) * (product.length || 0);
          totalVolume += volume;
          validProductCount++;
          
          // Count products
          const productKey = product.product_id;
          if (!productsInBasket[productKey]) {
            productsInBasket[productKey] = {
              name: product.product_name,
              image: product.image,
              count: 0
            };
          }
          productsInBasket[productKey].count += 1;
        });
        
        const basketVolume = basketData.width * basketData.height * basketData.length;
        const fillPercentage = basketVolume > 0 ? (totalVolume / basketVolume) * 100 : 0;
        
        basketData.fillPercentage = fillPercentage;
        basketData.remainingVolume = basketVolume - totalVolume;
        basketData.totalVolume = basketVolume;
        basketData.usedVolume = totalVolume;
        basketData.products = Object.values(productsInBasket);
        basketData.totalProducts = validProductCount; // ✅ Changed from stockSnapshot.size
        
        basketsData.push(basketData);
      }
      
      setBaskets(basketsData);
    } catch (error) {
      console.error('Error fetching baskets:', error);
      setError('Failed to fetch baskets');
    } finally {
      setLoading(false);
    }
  };

  const checkBasketNumberExists = async (basketNo) => {
    const basketsRef = collection(db, 'stock_basket');
    const q = query(basketsRef, where('basket_number', '==', basketNo));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  };

  const validateDimensions = () => {
    const w = parseFloat(width);
    const h = parseFloat(height);
    const l = parseFloat(length);

    if (isNaN(w) || isNaN(h) || isNaN(l)) {
      return 'All dimensions must be valid numbers';
    }

    if (w <= 0 || h <= 0 || l <= 0) {
      return 'All dimensions must be greater than 0';
    }

    if (w > 100 || h > 100 || l > 100) {
      return '⚠️ WARNING: Dimensions seem unusually large. Incorrect information will result in punishment!';
    }

    if (w < 1 || h < 1 || l < 1) {
      return '⚠️ WARNING: Dimensions seem unusually small. Please verify measurements!';
    }

    return null;
  };

  const handleCreateBasket = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate basket number
    if (!basketNumber.trim()) {
      setError('Basket number is required');
      return;
    }

    // Check if basket number already exists
    const exists = await checkBasketNumberExists(basketNumber.trim());
    if (exists) {
      setError('⚠️ This basket number already exists! Please use a different number.');
      return;
    }

    // Validate dimensions
    const dimensionError = validateDimensions();
    if (dimensionError) {
      setError(dimensionError);
      return;
    }

    try {
      const basketData = {
        basket_type: basketType,
        basket_number: basketNumber.trim(),
        width: parseFloat(width),
        height: parseFloat(height),
        length: parseFloat(length),
        created_by: currentUser?.email || 'Unknown',
        created_at: Timestamp.now(),
        age_days: 0
      };

      await addDoc(collection(db, 'stock_basket'), basketData);
      
      setSuccess('✅ Basket created successfully!');
      
      // Reset form
      setBasketNumber('');
      setWidth('');
      setHeight('');
      setLength('');
      setBasketType('fresh');
      
      // Refresh baskets list
      fetchBaskets();
      
      // Close form after 2 seconds
      setTimeout(() => {
        setShowCreateForm(false);
        setSuccess('');
      }, 2000);
      
    } catch (error) {
      console.error('Error creating basket:', error);
      setError('Failed to create basket: ' + error.message);
    }
  };

  const printReceipt = (basket) => {
    const printWindow = window.open('', '_blank');
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Basket Receipt</title>
        <style>
          @media print {
            body { margin: 0; padding: 0; }
          }
          body {
            width: 3.45in;
            height: 4in;
            margin: 0;
            padding: 10px;
            font-family: 'Courier New', monospace;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
          }
          .header {
            font-size: 44px;
            font-weight: bold;
            text-align: center;
            margin-bottom: 15px;
            text-transform: uppercase;
            border-bottom: 2px dashed #000;
            padding-bottom: 10px;
          }
          .basket-number {
            font-size: 108px;
            font-weight: bold;
            text-align: center;
            margin: 20px 0;
            letter-spacing: 3px;
          }
          .details {
            font-size: 11px;
            margin-top: 15px;
            width: 100%;
            border-top: 2px dashed #000;
            padding-top: 10px;
          }
          .detail-row {
            display: flex;
            justify-content: space-between;
            margin: 5px 0;
          }
          .label {
            font-weight: bold;
            font-size: 14px;
          }
          .value {  
            font-weight: bold;
            font-size: 14px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${basket.basket_type.toUpperCase()} BASKET
        </div>
        <div class="basket-number">
          ${basket.basket_number}
        </div>
        <div class="details">
          <div class="detail-row">
            <span class="label">Width:</span>
            <span class="value">${basket.width}"</span>
          </div>
          <div class="detail-row">
            <span class="label">Height:</span>
            <span class="value">${basket.height}"</span>
          </div>
          <div class="detail-row">
            <span class="label">Length:</span>
            <span class="value">${basket.length}"</span>
          </div>
          <div class="detail-row">
            <span class="label">Created:</span>
            <span>${new Date(basket.created_at.toDate()).toLocaleDateString()}</span>
          </div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const getBasketAge = (createdAt) => {
    if (!createdAt) return 0;
    const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
    const now = new Date();
    return Math.floor((now - date) / (1000 * 60 * 60 * 24));
  };

  const getFillColor = (percentage) => {
    if (percentage >= 100) return { bg: 'bg-red-500', border: 'border-red-700', opacity: 'bg-opacity-80' };
    if (percentage >= 80) return { bg: 'bg-yellow-500', border: 'border-yellow-700', opacity: 'bg-opacity-70' };
    if (percentage >= 50) return { bg: 'bg-blue-500', border: 'border-blue-700', opacity: 'bg-opacity-60' };
    if (percentage > 0) return { bg: 'bg-green-500', border: 'border-green-700', opacity: 'bg-opacity-50' };
    return { bg: 'bg-gray-300', border: 'border-gray-500', opacity: 'bg-opacity-30' };
  };

  const Basket3DView = ({ basket }) => {
    const maxDimension = Math.max(basket.width, basket.height, basket.length);
    const scale = 200 / maxDimension;
    
    const scaledWidth = basket.width * scale;
    const scaledHeight = basket.height * scale;
    const scaledLength = basket.length * scale;
    
    const age = getBasketAge(basket.created_at);
    const fillPercentage = basket.fillPercentage || 0;
    const fillColor = getFillColor(fillPercentage);
    
    // Calculate liquid fill height based on percentage
    const liquidHeight = (scaledHeight * fillPercentage) / 100;

    return (
      <div className="relative" style={{ width: '250px', height: '250px' }}>
        <div className="absolute inset-0 flex items-center justify-center">
          {/* 3D Box representation */}
          <div className="relative" style={{ 
            transformStyle: 'preserve-3d',
            transform: 'rotateX(-20deg) rotateY(30deg)'
          }}>
            {/* Empty Container - Front face */}
            <div
              className="absolute bg-white bg-opacity-20 border-2 border-gray-400"
              style={{
                width: `${scaledWidth}px`,
                height: `${scaledHeight}px`,
                transform: `translateZ(${scaledLength/2}px)`
              }}
            >
              {/* Liquid Fill - Front */}
              <div
                className={`absolute bottom-0 left-0 right-0 ${fillColor.bg} ${fillColor.opacity} border-t-2 ${fillColor.border}`}
                style={{
                  height: `${liquidHeight}px`,
                  transition: 'height 0.3s ease-in-out'
                }}
              >
                <div className="flex items-center justify-center h-full text-white font-bold text-2xl drop-shadow-lg">
                  {basket.basket_number}
                </div>
              </div>
            </div>
            
            {/* Empty Container - Back face */}
            <div
              className="absolute bg-white bg-opacity-10 border-2 border-gray-400"
              style={{
                width: `${scaledWidth}px`,
                height: `${scaledHeight}px`,
                transform: `translateZ(-${scaledLength/2}px) rotateY(180deg)`
              }}
            >
              {/* Liquid Fill - Back */}
              <div
                className={`absolute bottom-0 left-0 right-0 ${fillColor.bg} bg-opacity-30`}
                style={{
                  height: `${liquidHeight}px`,
                  transition: 'height 0.3s ease-in-out'
                }}
              />
            </div>
            
            {/* Top face - shows liquid surface */}
            <div
              className="absolute bg-white bg-opacity-20 border-2 border-gray-400"
              style={{
                width: `${scaledWidth}px`,
                height: `${scaledLength}px`,
                transform: `rotateX(90deg) translateZ(${scaledHeight/2}px)`
              }}
            />
            
            {/* Liquid Top Surface - moves up as basket fills */}
            {fillPercentage > 0 && (
              <div
                className={`absolute ${fillColor.bg} ${fillColor.opacity} border-2 ${fillColor.border}`}
                style={{
                  width: `${scaledWidth}px`,
                  height: `${scaledLength}px`,
                  transform: `rotateX(90deg) translateZ(${scaledHeight/2 - liquidHeight}px)`,
                  transition: 'transform 0.3s ease-in-out'
                }}
              />
            )}
            
            {/* Bottom face */}
            <div
              className="absolute bg-gray-200 bg-opacity-50 border-2 border-gray-400"
              style={{
                width: `${scaledWidth}px`,
                height: `${scaledLength}px`,
                transform: `rotateX(90deg) translateZ(-${scaledHeight/2}px)`
              }}
            />
            
            {/* Empty Container - Right face */}
            <div
              className="absolute bg-white bg-opacity-15 border-2 border-gray-400"
              style={{
                width: `${scaledLength}px`,
                height: `${scaledHeight}px`,
                transform: `rotateY(90deg) translateZ(${scaledWidth/2}px)`
              }}
            >
              {/* Liquid Fill - Right */}
              <div
                className={`absolute bottom-0 left-0 right-0 ${fillColor.bg} bg-opacity-60`}
                style={{
                  height: `${liquidHeight}px`,
                  transition: 'height 0.3s ease-in-out'
                }}
              />
            </div>
            
            {/* Empty Container - Left face */}
            <div
              className="absolute bg-white bg-opacity-10 border-2 border-gray-400"
              style={{
                width: `${scaledLength}px`,
                height: `${scaledHeight}px`,
                transform: `rotateY(90deg) translateZ(-${scaledWidth/2}px)`
              }}
            >
              {/* Liquid Fill - Left */}
              <div
                className={`absolute bottom-0 left-0 right-0 ${fillColor.bg} bg-opacity-40`}
                style={{
                  height: `${liquidHeight}px`,
                  transition: 'height 0.3s ease-in-out'
                }}
              />
            </div>
          </div>
        </div>
        
        {/* Fill Percentage Badge */}
        <div className={`absolute top-2 left-2 ${
          fillPercentage >= 100 ? 'bg-red-600' :
          fillPercentage >= 80 ? 'bg-yellow-600' :
          fillPercentage >= 50 ? 'bg-blue-600' :
          fillPercentage > 0 ? 'bg-green-600' : 'bg-gray-600'
        } text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg`}>
          {fillPercentage.toFixed(1)}% Full
        </div>
        
        {/* Age badge */}
        <div className="absolute top-2 right-2 bg-gray-800 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
          {age}d old
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-2xl font-semibold text-gray-700">Loading baskets...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-md">
        <div className="px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Basket Management</h2>
              <p className="text-sm text-gray-600 mt-1">Manage storage baskets for inventory</p>
            </div>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create New Basket
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 py-6">
        {/* Create Basket Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Create New Basket</h3>
            
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                {success}
              </div>
            )}

            <form onSubmit={handleCreateBasket} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Basket Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Basket Type *
                  </label>
                  <select
                    value={basketType}
                    onChange={(e) => setBasketType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="fresh">Fresh</option>
                    <option value="return">Return</option>
                  </select>
                </div>

                {/* Basket Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Basket Number * (Cannot be edited later)
                  </label>
                  <input
                    type="number"
                    value={basketNumber}
                    onChange={(e) => setBasketNumber(e.target.value)}
                    placeholder="e.g., 001"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    min="1"
                  />
                </div>

                {/* Width */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Width (inches) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    placeholder="e.g., 12.5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Height */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Height (inches) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="e.g., 8.0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Length */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Length (inches) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={length}
                    onChange={(e) => setLength(e.target.value)}
                    placeholder="e.g., 15.0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-3 rounded">
                <p className="font-semibold">⚠️ Warning</p>
                <p className="text-sm">Providing incorrect dimensions will result in punishment. Please double-check all measurements.</p>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg transition duration-200"
                >
                  Create Basket
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setError('');
                    setSuccess('');
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg transition duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Baskets Grid */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            All Baskets ({baskets.length})
          </h3>
        </div>

        {baskets.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-600 text-lg">No baskets created yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {baskets.map((basket) => (
              <div
                key={basket.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-200"
              >
                {/* Basket Type Header */}
                <div className={`${basket.basket_type === 'fresh' ? 'bg-green-600' : 'bg-orange-600'} text-white px-4 py-3`}>
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg uppercase">{basket.basket_type}</span>
                    <span className="text-sm opacity-90">
                      {basket.totalProducts || 0} items
                    </span>
                  </div>
                </div>

                {/* 3D Visualization */}
                <div className="p-6 flex justify-center bg-gray-50">
                  <Basket3DView basket={basket} />
                </div>

                {/* Basket Details */}
                <div className="p-6 border-t">
                  <div className="text-center mb-4">
                    <div className="text-3xl font-bold text-gray-800 mb-2">
                      #{basket.basket_number}
                    </div>
                  </div>

                  {/* Capacity Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Capacity</span>
                      <span className="font-bold">
                        {basket.fillPercentage?.toFixed(1) || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                      <div 
                        className={`h-4 transition-all ${
                          basket.fillPercentage >= 100 ? 'bg-red-500' :
                          basket.fillPercentage >= 80 ? 'bg-yellow-500' :
                          basket.fillPercentage >= 50 ? 'bg-blue-500' :
                          basket.fillPercentage > 0 ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        style={{ width: `${Math.min(basket.fillPercentage || 0, 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Used: {basket.usedVolume?.toFixed(1) || 0} cu in</span>
                      <span>Free: {basket.remainingVolume?.toFixed(1) || 0} cu in</span>
                    </div>
                  </div>

                  {/* Products List */}
                  {basket.products && basket.products.length > 0 && (
                    <div className="mb-4 border-t pt-4">
                      <div className="text-xs font-semibold text-gray-700 mb-2">
                        Products in Basket:
                      </div>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {basket.products.map((product, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                            <img 
                              src={product.image || 'https://via.placeholder.com/40'} 
                              alt={product.name}
                              className="w-10 h-10 object-cover rounded border"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/40';
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-800 truncate">
                                {product.name}
                              </div>
                              <div className="text-xs text-gray-600">
                                Qty: {product.count}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dimensions */}
                  <div className="space-y-2 text-sm border-t pt-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dimensions:</span>
                      <span className="font-semibold">
                        {basket.width}" × {basket.height}" × {basket.length}"
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Volume:</span>
                      <span className="font-semibold">
                        {basket.totalVolume?.toFixed(1) || 0} cu in
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-gray-600">Created by:</span>
                      <span className="font-semibold text-xs">{basket.created_by}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Created:</span>
                      <span className="font-semibold text-xs">
                        {basket.created_at?.toDate().toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => printReceipt(basket)}
                    className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Print Receipt
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BasketManagement;